import { cleanDateForApi } from '@digitaltattoo/shared-utils';

type BookPiercingResponse = {
  success: boolean;
  httpStatus: number;
  code?: string;
  message?: string;
  data?: any;
  errors?: Array<{ field: string; message: string }>;
};

// Helper function to clean booking payload for PostgreSQL compatibility
function cleanBookingPayload(payload: any): any {
  const cleaned = { ...payload };
  
  // Clean dateOfBirth if present
  if (cleaned.dateOfBirth) {
    cleaned.dateOfBirth = cleanDateForApi(cleaned.dateOfBirth);
  }
  
  // Clean appointment date if present
  if (cleaned.appointmentDate) {
    cleaned.appointmentDate = cleanDateForApi(cleaned.appointmentDate);
  }
  
  // Clean any date fields in nested objects
  if (cleaned.client && cleaned.client.dateOfBirth) {
    cleaned.client.dateOfBirth = cleanDateForApi(cleaned.client.dateOfBirth);
  }
  
  return cleaned;
}

export const bookingApi = {
  async bookPiercing(payload: any): Promise<BookPiercingResponse> {
    // Clean payload for PostgreSQL compatibility
    const cleanedPayload = cleanBookingPayload(payload);
    
    console.log('Sending cleaned booking payload:', cleanedPayload);
    
    // Use direct fetch to always surface server validation messages even on 4xx
    const resp = await fetch('/api/appointments/book-piercing', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cleanedPayload)
    });
    const contentType = resp.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await resp.json() : { success: false, message: `HTTP ${resp.status}` };
    return { ...data, httpStatus: resp.status } as BookPiercingResponse;
  },
  async payDeposit(payload: { appointmentId: string; paymentMethod: string; transactionId?: string; gatewayResponse?: any }): Promise<{ success: boolean; message?: string; data?: any }> {
    const token = localStorage.getItem('client_token');
    const resp = await fetch('/api/online-booking/pay-deposit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
    const contentType = resp.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    return isJson ? await resp.json() : { success: false, message: `HTTP ${resp.status}` };
  },
  async createCheckoutSession(appointmentId: string): Promise<{ success: boolean; message?: string; data?: { provider?: 'STRIPE' | 'VIVA'; checkoutUrl: string; sessionId: string } }> {
    const resp = await fetch('/api/online-booking/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ appointmentId }),
    });
    const contentType = resp.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    return isJson ? await resp.json() : { success: false, message: `HTTP ${resp.status}` };
  },
  async verifyPayment(params: { sessionId?: string; appointmentId?: string; provider?: string; transactionRef?: string; status?: string; orderCode?: string; vivaTransactionId?: string }): Promise<{ success: boolean; data?: any; message?: string }> {
    const query = new URLSearchParams();
    if (params.sessionId) query.set('session_id', params.sessionId);
    if (params.appointmentId) query.set('appointment_id', params.appointmentId);
    if (params.provider) query.set('provider', params.provider);
    if (params.transactionRef) query.set('transaction_ref', params.transactionRef);
    if (params.status) query.set('status', params.status);
    if (params.orderCode) query.set('s', params.orderCode);
    if (params.vivaTransactionId) query.set('t', params.vivaTransactionId);
    const resp = await fetch(`/api/online-booking/verify-payment?${query.toString()}`);
    const contentType = resp.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    return isJson ? await resp.json() : { success: false, message: `HTTP ${resp.status}` };
  },
  async checkPiercingConflict(payload: any): Promise<{ success: boolean; data?: any; message?: string }> {
    const resp = await fetch('/api/appointments/piercing-conflict', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const contentType = resp.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await resp.json() : { success: false, message: `HTTP ${resp.status}` };
    return data as { success: boolean; data?: any; message?: string };
  },
};
